/**
 * Two-Factor Authentication API Service
 *
 * This service provides functions to interact with the 2FA backend API endpoints.
 * All endpoints require authentication except those called during login flow.
 */

import api, { public_api } from '../api/api';
import type {
  User2FAStatusResponse,
  TwoFactorMyStatusResponse,
  TwoFactorSetupResponse,
  TwoFactorCompleteResponse,
  TwoFactorCheckResponse,
  TwoFactorUsersListResponse,
  TwoFactorEnableDisableResponse,
} from '../types/auth';

// ============================================
// PUBLIC 2FA ENDPOINTS (Session-Based)
// ============================================

/**
 * Verify OTP during login
 * POST /core/public/v1/account/2fa/verify
 */
export const verifyTwoFactorOtp = async (
  sessionId: string,
  otpCode: string
): Promise<any> => {
  const response = await public_api.post('/account/2fa/verify', {
    session_id: sessionId,
    otp_code: otpCode,
  });
  return response.data;
};

/**
 * Initiate 2FA setup during login (session-based)
 * POST /core/public/v1/account/2fa/setup/initiate
 */
export const initiatePublicTwoFactorSetup = async (sessionId: string): Promise<TwoFactorSetupResponse> => {
  const response = await public_api.post<TwoFactorSetupResponse>('/account/2fa/setup/initiate', {
    session_id: sessionId,
  });
  return response.data;
};

/**
 * Complete 2FA setup during login (session-based)
 * POST /core/public/v1/account/2fa/setup/complete
 */
export const completePublicTwoFactorSetup = async (
  sessionId: string,
  secret: string,
  code: string,
  otpMethod?: string
): Promise<any> => {
  const payload: any = {
    session_id: sessionId,
    code,
  };

  // Only include secret for TOTP, not for email OTP
  if (otpMethod === 'totp' && secret) {
    payload.secret = secret;
  }

  // Include OTP method if specified
  if (otpMethod) {
    payload.otp_method = otpMethod;
  }

  const response = await public_api.post('/account/2fa/setup/complete', payload);
  return response.data;
};

/**
 * Resend email OTP during login
 * POST /core/public/v1/account/2fa/email/resend
 */
export const resendEmailOTP = async (sessionId: string): Promise<any> => {
  const response = await public_api.post('/account/2fa/email/resend', {
    session_id: sessionId,
  });
  return response.data;
};

// ============================================
// USER SELF-SERVICE ENDPOINTS
// ============================================

/**
 * Get current user's 2FA status
 * GET /core/api/v1/auth/2fa/status
 */
export const getMyTwoFactorStatus = async (): Promise<TwoFactorMyStatusResponse> => {
  const response = await api.get<TwoFactorMyStatusResponse>('/auth/2fa/status');
  return response.data;
};

/**
 * Initiate 2FA setup for current user (TOTP or Email)
 * POST /core/api/v1/auth/2fa/setup/initiate
 * @param selfSetup - If true, allows setup without prior 2FA enable (user self-enabling flow)
 * @param otpMethod - 'totp' for authenticator app, 'email' for email OTP
 */
export const initiateTwoFactorSetup = async (
  selfSetup?: boolean,
  otpMethod?: 'totp' | 'email'
): Promise<TwoFactorSetupResponse> => {
  const response = await api.post<TwoFactorSetupResponse>('/auth/2fa/setup/initiate', {
    self_setup: selfSetup || false,
    otp_method: otpMethod || 'totp',
  });
  return response.data;
};

/**
 * Complete 2FA setup for current user (TOTP or Email)
 * POST /core/api/v1/auth/2fa/setup/complete
 * @param selfSetup - If true, backend also enables 2FA (user self-enabling flow)
 * @param otpMethod - 'totp' for authenticator app, 'email' for email OTP
 */
export const completeTwoFactorSetup = async (
  secret: string,
  code: string,
  selfSetup?: boolean,
  otpMethod?: 'totp' | 'email'
): Promise<TwoFactorCompleteResponse> => {
  const response = await api.post<TwoFactorCompleteResponse>('/auth/2fa/setup/complete', {
    secret,
    code,
    self_setup: selfSetup || false,
    otp_method: otpMethod || 'totp',
  });
  return response.data;
};

/**
 * Resend email OTP during authenticated self-setup
 * POST /core/api/v1/auth/2fa/setup/email/resend
 */
export const resendSetupEmailOTP = async (): Promise<any> => {
  const response = await api.post('/auth/2fa/setup/email/resend');
  return response.data;
};

/**
 * Check if 2FA is required for current user
 * GET /core/api/v1/auth/2fa/check
 */
export const checkTwoFactorRequirement = async (): Promise<TwoFactorCheckResponse> => {
  const response = await api.get<TwoFactorCheckResponse>('/auth/2fa/check');
  return response.data;
};

/**
 * Update current user's OTP preference
 * POST /core/api/v1/auth/2fa/user/otp-preference
 */
export const updateUserOtpPreference = async (otpPreference: string): Promise<any> => {
  const response = await api.post('/auth/2fa/user/otp-preference', {
    otp_preference: otpPreference,
  });
  return response.data;
};

// ============================================
// USER MANAGEMENT ENDPOINTS (Admin/Manager)
// ============================================

/**
 * Get 2FA status for a specific user
 * GET /core/api/v1/users/:userId/2fa/status
 */
export const getUserTwoFactorStatus = async (userId: string): Promise<User2FAStatusResponse> => {
  const response = await api.get<User2FAStatusResponse>(`/users/${userId}/2fa/status`);
  return response.data;
};

/**
 * Enable 2FA for a specific user
 * POST /core/api/v1/users/:userId/2fa/enable
 */
export const enableTwoFactorForUser = async (userId: string): Promise<TwoFactorEnableDisableResponse> => {
  const response = await api.post<TwoFactorEnableDisableResponse>(`/users/${userId}/2fa/enable`);
  return response.data;
};

/**
 * Disable 2FA for a specific user
 * POST /core/api/v1/users/:userId/2fa/disable
 */
export const disableTwoFactorForUser = async (
  userId: string,
  keycloakUserId: string
): Promise<TwoFactorEnableDisableResponse> => {
  const response = await api.post<TwoFactorEnableDisableResponse>(`/users/${userId}/2fa/disable`, {
    keycloak_user_id: keycloakUserId,
  });
  return response.data;
};

/**
 * List all users with 2FA enabled
 * GET /core/api/v1/users/2fa/enabled
 */
export const listUsersWithTwoFactorEnabled = async (): Promise<TwoFactorUsersListResponse> => {
  const response = await api.get<TwoFactorUsersListResponse>('/users/2fa/enabled');
  return response.data;
};

/**
 * List users with incomplete 2FA setup
 * GET /core/api/v1/users/2fa/incomplete-setup
 */
export const listUsersWithIncompleteTwoFactorSetup = async (): Promise<TwoFactorUsersListResponse> => {
  const response = await api.get<TwoFactorUsersListResponse>('/users/2fa/incomplete-setup');
  return response.data;
};

/**
 * Update OTP preference for a specific user
 * POST /core/api/v1/users/:userId/2fa/otp-preference
 */
export const adminUpdateUserOtpPreference = async (
  userId: string,
  otpPreference: string
): Promise<any> => {
  const response = await api.post(`/users/${userId}/2fa/otp-preference`, {
    otp_preference: otpPreference,
  });
  return response.data;
};

// ============================================
// BULK MFA OPERATIONS
// ============================================

export interface BulkMfaResponse {
  success: boolean;
  message: string;
  enabled_count?: number;
  disabled_count?: number;
  skipped_count?: number;
  skipped_reason?: string;
  error?: string;
}

/**
 * Bulk enable MFA for multiple users
 * POST /core/api/v1/users/2fa/bulk-enable
 */
export const bulkEnableMfa = async (userUuids: string[]): Promise<BulkMfaResponse> => {
  const response = await api.post<BulkMfaResponse>('/users/2fa/bulk-enable', {
    user_uuids: userUuids,
  });
  return response.data;
};

/**
 * Bulk disable MFA for multiple users
 * POST /core/api/v1/users/2fa/bulk-disable
 */
export const bulkDisableMfa = async (userUuids: string[]): Promise<BulkMfaResponse> => {
  const response = await api.post<BulkMfaResponse>('/users/2fa/bulk-disable', {
    user_uuids: userUuids,
  });
  return response.data;
};
